// Update Feed API service — framework-agnostic fetch of feed pages + update
// detail. Mirrors groups-api.js / collection-api.js. Talks to the thin
// updates_api Cloud Function; scope is a query param on one route.

import { API_ENDPOINTS } from './endpoints.js';
import { apiJson } from './api-fetch.js';

/**
 * @param {{ scope?: 'group'|'binder', groupId?: string|null, binderUrl?: string|null,
 *           subgroupIds?: string[], page?: number, pageSize?: number }} opts
 * @returns {string}
 */
function buildFeedUrl({
  scope,
  groupId = null,
  binderUrl = null,
  subgroupIds = [],
  page = 1,
  pageSize = 20,
}) {
  const sp = new URLSearchParams();
  if (scope === 'group' && groupId) {
    sp.set('group', groupId);
    if (subgroupIds.length > 0) sp.set('subgroupIds', subgroupIds.join(','));
  }
  if (scope === 'binder' && binderUrl) sp.set('binder', binderUrl);
  if (page && page !== 1) sp.set('page', String(page));
  if (pageSize && pageSize !== 20) sp.set('pageSize', String(pageSize));
  const qs = sp.toString();
  return qs ? `${API_ENDPOINTS.updates_api}?${qs}` : API_ENDPOINTS.updates_api;
}

/**
 * Fetch a page of Binder Updates for a scope. A scope should always be given
 * (there is no "my feed"); a call without one requests the bare feed URL,
 * which the tests exercise.
 * @param {Object} [opts]
 * @param {'group'|'binder'} [opts.scope]
 * @param {string|null} [opts.groupId] - required for scope 'group'
 * @param {string|null} [opts.binderUrl] - short URL (e.g. '/mfb.abc'), scope 'binder'
 * @param {string[]} [opts.subgroupIds] - scope 'group' only: the picker's
 *   subgroup selection ([] = whole server); a foreign id is a 400
 * @param {number} [opts.page] - 1-indexed page
 * @param {number} [opts.pageSize]
 * @returns {Promise<{ updates: Array, page: number, pageSize: number, total: number, totalPages: number }>}
 */
export async function fetchUpdateFeed(opts = {}) {
  const data = await apiJson(buildFeedUrl(opts));
  return {
    updates: data.updates || [],
    page: data.page,
    pageSize: data.pageSize,
    total: data.total,
    totalPages: data.totalPages,
  };
}

/**
 * Fetch a single Binder Update's full Card Change list, keyed by its binder's
 * short URL and per-binder seq (the values in the /{short}/updates/{seq} URL).
 * @param {string} binderUrl - short URL (e.g. '/mfb.abc')
 * @param {string|number} seq - per-binder sequence number
 * @returns {Promise<{ update: Object, changes: Array }>}
 */
export async function fetchUpdateDetail(binderUrl, seq) {
  const url =
    `${API_ENDPOINTS.updates_api}?binder=${encodeURIComponent(binderUrl)}` +
    `&seq=${encodeURIComponent(seq)}`;
  const data = await apiJson(url);
  return { update: data.update, changes: data.changes || [] };
}
